import React, { createContext, useContext, useState, useEffect } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);

  // Check for pending transactions on page load
  useEffect(() => {
    const checkPendingTransaction = () => {
      const pendingTransaction = localStorage.getItem('pendingMvolaTransaction');
      if (pendingTransaction) {
        try {
          const transactionData = JSON.parse(pendingTransaction);
          const { dpAmount, timestamp } = transactionData;
          
          // Check if transaction is still within timeout period (120 seconds)
          const now = Date.now();
          const transactionAge = now - timestamp;
          const timeoutPeriod = 120000; // 120 seconds (2 minutes)
          
          if (transactionAge < timeoutPeriod) {
            // Show pending notification
            setNotification({
              type: 'pending',
              message: 'Transaction en cours, attendez svp...',
              dpAmount,
              timestamp
            });
            
            // Set timeout for remaining time
            const remainingTime = timeoutPeriod - transactionAge;
            setTimeout(() => {
              setNotification({
                type: 'failed',
                message: 'Transaction échouée. Veuillez réessayer.',
                dpAmount,
                timestamp: Date.now()
              });
              
              // Auto-hide after 5 seconds
              setTimeout(() => {
                setNotification(null);
                localStorage.removeItem('pendingMvolaTransaction');
              }, 5000);
            }, remainingTime);
          } else {
            // Transaction has timed out, clean up
            localStorage.removeItem('pendingMvolaTransaction');
          }
        } catch (error) {
          console.error('Error parsing pending transaction:', error);
          localStorage.removeItem('pendingMvolaTransaction');
        }
      }
    };

    checkPendingTransaction();
  }, []);

  const showNotification = (type, message, dpAmount = null) => {
    setNotification({
      type,
      message,
      dpAmount,
      timestamp: Date.now()
    });
  };

  const hideNotification = () => {
    setNotification(null);
  };

  const showTransactionPending = (dpAmount) => {
    // Store pending transaction in localStorage
    const pendingTransaction = {
      dpAmount,
      timestamp: Date.now()
    };
    localStorage.setItem('pendingMvolaTransaction', JSON.stringify(pendingTransaction));
    
    showNotification('pending', 'Transaction en cours, attendez svp...', dpAmount);
  };

  const showTransactionSuccess = (dpAmount) => {
    // Clear pending transaction from localStorage
    localStorage.removeItem('pendingMvolaTransaction');
    
    showNotification('success', `Transaction réussie! ${dpAmount} DP ajoutés à votre compte.`, dpAmount);
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      hideNotification();
    }, 5000);
  };

  const showTransactionFailed = (dpAmount) => {
    // Clear pending transaction from localStorage
    localStorage.removeItem('pendingMvolaTransaction');
    
    showNotification('failed', 'Transaction échouée. Veuillez réessayer.', dpAmount);
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      hideNotification();
    }, 5000);
  };

  return (
    <NotificationContext.Provider value={{
      notification,
      showNotification,
      hideNotification,
      showTransactionPending,
      showTransactionSuccess,
      showTransactionFailed
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
